import React, { useState, useEffect } from 'react';
import apiClient from '../../api/apiClient';
import { OPENAI_CHAT_MODEL_OPTIONS } from '../../constants/openaiModels';
import { CLAUDE_MODEL_OPTIONS, GEMINI_MODEL_OPTIONS } from '../../constants/providerModels';

const VariableManager = ({ 
  step, 
  onUpdateStep, 
  availableVariables = [], 
  projectId,
  apiEndpoints = []
}) => {
  const [responseVariables, setResponseVariables] = useState(
    step?.variableMapping?.saveFromResponse || []
  );
  const [inputVariables, setInputVariables] = useState(() => {
    // Ensure all existing input variables have the extractionModel and required fields
    const existingVariables = step?.variableMapping?.saveFromInput || [];
    return existingVariables.map(variable => ({
      variableName: variable.variableName || '',
      inputType: variable.inputType || 'direct_input',
      extractionPattern: variable.extractionPattern || '',
      extractionPrompt: variable.extractionPrompt || '',
      extractionModel: variable.extractionModel || '', // Ensure this field exists
      description: variable.description || '',
      required: variable.required !== false, // FIXED: Include the required field
      conditionalRequirements: variable.conditionalRequirements || [], // 🔧 CRITICAL FIX: Preserve conditionalRequirements
      conditionalRequirementsLogic: variable.conditionalRequirementsLogic || 'OR' // 🔧 CRITICAL FIX: Preserve conditionalRequirementsLogic
    }));
  });
  const [selectedApiEndpoint, setSelectedApiEndpoint] = useState(null);
  const [apiResponseStructure, setApiResponseStructure] = useState(null);
  const [showVariableDropdown, setShowVariableDropdown] = useState(false);

  // Load API endpoint details when step changes
  useEffect(() => {
    if (step?.apiCall?.apiEndpoint) {
      loadApiEndpointDetails(step.apiCall.apiEndpoint);
    }
  }, [step?.apiCall?.apiEndpoint]);

  // Update input variables when step changes (normalize missing fields)
  useEffect(() => {
    const existingVariables = step?.variableMapping?.saveFromInput || [];
    const normalizedVariables = existingVariables.map(variable => ({
      variableName: variable.variableName || '',
      inputType: variable.inputType || 'direct_input',
      extractionPattern: variable.extractionPattern || '',
      extractionPrompt: variable.extractionPrompt || '',
      extractionModel: variable.extractionModel || '', // Ensure this field exists
      description: variable.description || '',
      required: variable.required !== false, // FIXED: Include the required field
      conditionalRequirements: variable.conditionalRequirements || [], // 🔧 CRITICAL FIX: Preserve conditionalRequirements
      conditionalRequirementsLogic: variable.conditionalRequirementsLogic || 'OR' // 🔧 CRITICAL FIX: Preserve conditionalRequirementsLogic
    }));
    setInputVariables(normalizedVariables);
  }, [step?.variableMapping?.saveFromInput]);

  const loadApiEndpointDetails = async (endpointId) => {
    try {
      const response = await apiClient.get(`/projects/${projectId}/api-endpoints/${endpointId}`);
      setSelectedApiEndpoint(response.data.apiEndpoint);
      
      // Parse sample response to help with variable mapping
      if (response.data.apiEndpoint.sampleResponse) {
        try {
          const sampleResponse = JSON.parse(response.data.apiEndpoint.sampleResponse);
          setApiResponseStructure(analyzeResponseStructure(sampleResponse));
        } catch (e) {
          console.warn('Could not parse sample response:', e);
        }
      }
    } catch (error) {
      console.error('Error loading API endpoint:', error);
    }
  };

  // Analyze response structure to suggest variable paths
  const analyzeResponseStructure = (obj, path = '', level = 0) => {
    const structure = [];
    
    if (level > 5) return structure; // Prevent infinite recursion
    
    if (Array.isArray(obj)) {
      structure.push({
        path: path,
        type: 'array',
        description: `Array with ${obj.length} items`,
        sampleValue: obj.slice(0, 2) // Show first 2 items
      });
      
      if (obj.length > 0) {
        const firstItem = obj[0];
        const itemStructure = analyzeResponseStructure(firstItem, `${path}[0]`, level + 1);
        structure.push(...itemStructure.map(item => ({
          ...item,
          path: item.path.replace('[0]', '[*]'),
          isArrayItem: true
        })));
      }
    } else if (obj && typeof obj === 'object') {
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        const currentPath = path ? `${path}.${key}` : key;
        
        if (Array.isArray(value)) {
          structure.push({
            path: currentPath,
            type: 'array',
            description: `Array with ${value.length} items`,
            sampleValue: value.slice(0, 2)
          });
          
          if (value.length > 0) {
            const itemStructure = analyzeResponseStructure(value[0], `${currentPath}[0]`, level + 1);
            structure.push(...itemStructure.map(item => ({
              ...item,
              path: item.path.replace('[0]', '[*]'),
              isArrayItem: true
            })));
          }
        } else if (value && typeof value === 'object') {
          structure.push({
            path: currentPath,
            type: 'object',
            description: `Object with ${Object.keys(value).length} properties`,
            sampleValue: value
          });
          
          const nestedStructure = analyzeResponseStructure(value, currentPath, level + 1);
          structure.push(...nestedStructure);
        } else {
          structure.push({
            path: currentPath,
            type: typeof value,
            description: `${typeof value} value`,
            sampleValue: value
          });
        }
      });
    }
    
    return structure;
  };

  const addResponseVariable = () => {
    const newVariable = {
      variableName: '',
      sourcePath: '',
      dataType: 'string',
      description: '',
      arrayHandling: {
        selectionType: 'first',
        selectionMapping: {}
      }
    };
    const updatedVariables = [...responseVariables, newVariable];
    setResponseVariables(updatedVariables);
    
    // Immediately update the step with the new variable
    if (onUpdateStep) {
      onUpdateStep({
        ...step,
        variableMapping: {
          ...step.variableMapping,
          saveFromResponse: updatedVariables,
          saveFromInput: inputVariables,
          availableVariables: availableVariables
        }
      });
    }
  };

  const updateResponseVariable = (index, field, value) => {
    const updated = [...responseVariables];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updated[index][parent] = { ...updated[index][parent], [child]: value };
    } else {
      updated[index][field] = value;
    }
    setResponseVariables(updated);
    updateStepVariableMappingWithData(updated, inputVariables);
  };

  const removeResponseVariable = (index) => {
    const updated = responseVariables.filter((_, i) => i !== index);
    setResponseVariables(updated);
    updateStepVariableMappingWithData(updated, inputVariables);
  };

  const addInputVariable = () => {
    const newVariable = {
      variableName: '',
      inputType: 'direct_input',
      extractionPattern: '',
      extractionPrompt: '',
      extractionModel: '',
      description: '',
      required: true, // Default new variables to required
      conditionalRequirements: [], // 🔧 CRITICAL FIX: Initialize empty conditionalRequirements for new variables
      conditionalRequirementsLogic: 'OR' // 🔧 CRITICAL FIX: Initialize conditionalRequirementsLogic for new variables
    };
    const updatedVariables = [...inputVariables, newVariable];
    setInputVariables(updatedVariables);
    updateStepVariableMappingWithData(responseVariables, updatedVariables);
  };

  const updateInputVariable = (index, field, value) => {
    const updated = [...inputVariables];
    updated[index][field] = value;
    setInputVariables(updated);
    updateStepVariableMappingWithData(responseVariables, updated);
  };

  const removeInputVariable = (index) => {
    const updated = inputVariables.filter((_, i) => i !== index);
    setInputVariables(updated);
    updateStepVariableMappingWithData(responseVariables, updated);
  };

  const updateStepVariableMapping = () => {
    updateStepVariableMappingWithData(responseVariables, inputVariables);
  };

  const updateStepVariableMappingWithData = (responseVars, inputVars) => {
    if (onUpdateStep) {
      onUpdateStep({
        ...step,
        variableMapping: {
          ...step.variableMapping,
          saveFromResponse: responseVars,
          saveFromInput: inputVars,
          availableVariables: availableVariables
        }
      });
    }
  };

  const insertVariableReference = (variableName, targetField) => {
    const reference = `{{${variableName}}}`;
    // This would typically update the field where the cursor is positioned
    // For now, we'll just copy to clipboard
    navigator.clipboard.writeText(reference);
    alert(`Variable reference {{${variableName}}} copied to clipboard`);
  };

  return (
    <div className="space-y-6 bg-gray-50 p-4 rounded-lg">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Variable Management</h3>
        <div className="relative">
          <button
            onClick={() => setShowVariableDropdown(!showVariableDropdown)}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
          >
            📋 Available Variables ({availableVariables.length})
          </button>
          
          {showVariableDropdown && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-10">
              <div className="p-3 border-b">
                <h4 className="font-medium text-gray-900">Available Variables</h4>
                <p className="text-sm text-gray-600">Click to copy {`{{variableName}}`} reference</p>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {availableVariables.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500">
                    No variables available from previous steps
                  </div>
                ) : (
                  availableVariables.map((variable, index) => {
                    // Handle different variable formats (name vs variableName)
                    const varName = variable.variableName || variable.name;
                    const varType = variable.dataType || variable.type;
                    const varDescription = variable.description || variable.source;
                    
                    return (
                      <div
                        key={index}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                        onClick={() => insertVariableReference(varName)}
                      >
                        <div className="font-medium text-sm">{`{{${varName}}}`}</div>
                        <div className="text-xs text-gray-500">{varType}</div>
                        {varDescription && (
                          <div className="text-xs text-gray-600 mt-1">{varDescription}</div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Variables to save from API Response */}
      {(step?.type === 'api_call') && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">Save Variables from API Response</h4>
            <button
              onClick={addResponseVariable}
              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
            >
              + Add Variable
            </button>
          </div>

          {/* API Response Structure Helper */}
          {apiResponseStructure && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h5 className="font-medium text-blue-900 mb-2">Suggested Paths from API Response:</h5>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {apiResponseStructure.slice(0, 10).map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-blue-800">{item.path}</span>
                    <span className="text-blue-600">({item.type})</span>
                  </div>
                ))}
                {apiResponseStructure.length > 10 && (
                  <div className="text-xs text-blue-600">+{apiResponseStructure.length - 10} more paths...</div>
                )}
              </div>
            </div>
          )}

          {responseVariables.map((variable, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-medium text-gray-900">Response Variable {index + 1}</h5>
                <button
                  onClick={() => removeResponseVariable(index)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  🗑️ Remove
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Variable Name
                  </label>
                  <input
                    type="text"
                    value={variable.variableName}
                    onChange={(e) => updateResponseVariable(index, 'variableName', e.target.value)}
                    placeholder="e.g., selectedDoctor"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Source Path
                  </label>
                  <input
                    type="text"
                    value={variable.sourcePath}
                    onChange={(e) => updateResponseVariable(index, 'sourcePath', e.target.value)}
                    placeholder="e.g., data[0].id or response.doctors[*].name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Type
                  </label>
                  <select
                    value={variable.dataType}
                    onChange={(e) => updateResponseVariable(index, 'dataType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="string">String</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="object">Object</option>
                    <option value="array">Array</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Array Handling
                  </label>
                  <select
                    value={variable.arrayHandling.selectionType}
                    onChange={(e) => updateResponseVariable(index, 'arrayHandling.selectionType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="first">Take First Item</option>
                    <option value="last">Take Last Item</option>
                    <option value="all">Take All Items</option>
                    <option value="user_selection">User Selection</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={variable.description}
                  onChange={(e) => updateResponseVariable(index, 'description', e.target.value)}
                  placeholder="Describe what this variable contains"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Variables to save from User Input */}
      {(step?.type === 'collect_input' || step?.type === 'message') && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">Save Variables from User Input</h4>
            <button
              onClick={addInputVariable}
              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
            >
              + Add Variable
            </button>
          </div>

          {inputVariables.map((variable, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-medium text-gray-900">Input Variable {index + 1}</h5>
                <button
                  onClick={() => removeInputVariable(index)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  🗑️ Remove
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Variable Name
                  </label>
                  <input
                    type="text"
                    value={variable.variableName}
                    onChange={(e) => updateInputVariable(index, 'variableName', e.target.value)}
                    placeholder="e.g., userLocation"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Input Type
                  </label>
                  <select
                    value={variable.inputType}
                    onChange={(e) => updateInputVariable(index, 'inputType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="direct_input">Direct Input</option>
                    <option value="choice_selection">Choice Selection</option>
                    <option value="extracted_value">Extracted Value</option>
                    <option value="ai_extract">AI Extraction</option>
                  </select>
                </div>
              </div>

              {variable.inputType === 'extracted_value' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Extraction Pattern (Regex)
                  </label>
                  <input
                    type="text"
                    value={variable.extractionPattern}
                    onChange={(e) => updateInputVariable(index, 'extractionPattern', e.target.value)}
                    placeholder="e.g., \\d+ for numbers"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {variable.inputType === 'ai_extract' && (
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Extraction Prompt
                    </label>
                    <textarea
                      value={variable.extractionPrompt || ''}
                      onChange={(e) => updateInputVariable(index, 'extractionPrompt', e.target.value)}
                      placeholder="e.g., Extract the phone number from the user's message"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Describe what specific information to extract from the user's input
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      LLM Model for Extraction
                    </label>
                    <select
                      value={variable.extractionModel || ''}
                      onChange={(e) => updateInputVariable(index, 'extractionModel', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Use Project Default</option>
                      <optgroup label="OpenAI Models">
                        {OPENAI_CHAT_MODEL_OPTIONS.map((model) => (
                          <option key={model.value} value={model.value}>{model.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="DeepSeek Models">
                        <option value="deepseek-chat">DeepSeek Chat</option>
                        <option value="deepseek-reasoner">DeepSeek Reasoner</option>
                      </optgroup>
                      <optgroup label="Qwen Models">
                        <option value="qwen-max">Qwen Max</option>
                        <option value="qwen-plus">Qwen Plus</option>
                        <option value="qwen-turbo">Qwen Turbo</option>
                      </optgroup>
                      <optgroup label="Claude Models">
                        {CLAUDE_MODEL_OPTIONS.map((model) => (
                          <option key={model.value} value={model.value}>{model.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Gemini Models">
                        {GEMINI_MODEL_OPTIONS.map((model) => (
                          <option key={model.value} value={model.value}>{model.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Gemma Models">
                        <option value="gemma-2">Gemma 2</option>
                        <option value="gemma-3">Gemma 3</option>
                      </optgroup>
                      <optgroup label="Meta Llama Models">
                        <option value="meta-llama/Llama-3-70b-chat-hf">Llama 3 70B Chat</option>
                        <option value="meta-llama/Llama-3-8b-chat-hf">Llama 3 8B Chat</option>
                        <option value="meta-llama/Meta-Llama-3-8B-Instruct-Lite">Llama 3 8B Instruct Lite</option>
                        <option value="meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo">Llama 3.1 8B Instruct Turbo</option>
                        <option value="meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo">Llama 3.1 70B Instruct Turbo</option>
                        <option value="meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo">Llama 3.1 405B Instruct Turbo</option>
                        <option value="meta-llama/Llama-3.2-11B-Vision-Instruct-Turbo">Llama 3.2 11B Vision Instruct</option>
                        <option value="meta-llama/Llama-3.3-70B-Instruct-Turbo">Llama 3.3 70B Instruct Turbo</option>
                        <option value="meta-llama/llama-4-scout">Llama 4 Scout</option>
                        <option value="meta-llama/llama-4-maverick">Llama 4 Maverick</option>
                      </optgroup>
                      <optgroup label="NVIDIA Models">
                        <option value="nvidia/Llama-3.1-Nemotron-70B-Instruct-HF">Nemotron 70B Instruct</option>
                        <option value="nvidia/llama-3.1-nemotron-70b-instruct">Nemotron 70B Instruct (Alt)</option>
                      </optgroup>
                      <optgroup label="Other Models">
                        <option value="MythoMax-L2-13b-Lite">MythoMax L2 13B Lite</option>
                      </optgroup>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Choose the LLM model to use for extraction. If not specified, the project's primary LLM will be used.
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={variable.description}
                  onChange={(e) => updateInputVariable(index, 'description', e.target.value)}
                  placeholder="Describe what this variable contains"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Required Toggle */}
              <div className="mt-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`required-${index}`}
                    checked={variable.required !== false} // Default to true (required) unless explicitly set to false
                    onChange={(e) => {
                      console.log('🔧 [VariableManager] REQUIRED VARIABLE CHECKBOX CLICKED!');
                      console.log('🔧 [VariableManager] Variable:', variable.variableName);
                      console.log('🔧 [VariableManager] Index:', index);
                      console.log('🔧 [VariableManager] Current required:', variable.required);
                      console.log('🔧 [VariableManager] New required:', e.target.checked);
                      
                      updateInputVariable(index, 'required', e.target.checked);
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`required-${index}`} className="ml-2 block text-sm text-gray-700">
                    Required field
                  </label>
                </div>
              </div>

              {/* Conditional Required Configuration */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Conditional Requirements
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      console.log('🔧 [VariableManager] ADD CONDITION for variable:', variable.variableName);
                      
                      const conditionalRequirements = variable.conditionalRequirements || [];
                      const newCondition = {
                        variable: '',
                        operator: 'exists',
                        value: '',
                        makeRequired: true
                      };
                      const updatedConditions = [...conditionalRequirements, newCondition];
                      
                      updateInputVariable(index, 'conditionalRequirements', updatedConditions);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    + Add Condition
                  </button>
                </div>
                
                {(variable.conditionalRequirements || []).length > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-3">
                    <div className="text-xs text-gray-600 mb-2">
                      This variable will be required when the following conditions are met:
                    </div>
                    
                    {/* Logic Operator Selection (only show if multiple conditions exist) */}
                    {(variable.conditionalRequirements || []).length > 1 && (
                      <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Logic for Multiple Conditions
                        </label>
                        <select
                          value={variable.conditionalRequirementsLogic || 'OR'}
                          onChange={(e) => {
                            updateInputVariable(index, 'conditionalRequirementsLogic', e.target.value);
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="OR">ANY condition (OR)</option>
                          <option value="AND">ALL conditions (AND)</option>
                        </select>
                        <div className="text-xs text-gray-600 mt-1">
                          {variable.conditionalRequirementsLogic === 'AND' 
                            ? 'All conditions must be true to make this variable required'
                            : 'Any single condition being true will make this variable required'
                          }
                        </div>
                      </div>
                    )}
                    
                    {(variable.conditionalRequirements || []).map((condition, condIndex) => (
                      <div key={condIndex} className="bg-white border border-gray-200 rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-700">Condition {condIndex + 1}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedConditions = [...(variable.conditionalRequirements || [])];
                              updatedConditions.splice(condIndex, 1);
                              updateInputVariable(index, 'conditionalRequirements', updatedConditions);
                            }}
                            className="text-red-600 hover:text-red-800 text-xs"
                          >
                            🗑️ Remove
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                          {/* Variable Selection */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Variable
                            </label>
                            <select
                              value={condition.variable || ''}
                              onChange={(e) => {
                                const updatedConditions = [...(variable.conditionalRequirements || [])];
                                updatedConditions[condIndex] = {
                                  ...updatedConditions[condIndex],
                                  variable: e.target.value
                                };
                                updateInputVariable(index, 'conditionalRequirements', updatedConditions);
                              }}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select variable...</option>
                              {availableVariables.map((availableVar) => (
                                <option key={availableVar.name} value={availableVar.name}>
                                  {availableVar.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          {/* Operator Selection */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Condition
                            </label>
                            <select
                              value={condition.operator || 'exists'}
                              onChange={(e) => {
                                const updatedConditions = [...(variable.conditionalRequirements || [])];
                                updatedConditions[condIndex] = {
                                  ...updatedConditions[condIndex],
                                  operator: e.target.value
                                };
                                updateInputVariable(index, 'conditionalRequirements', updatedConditions);
                              }}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="exists">exists</option>
                              <option value="not_exists">does not exist</option>
                              <option value="equals">equals</option>
                              <option value="not_equals">does not equal</option>
                              <option value="true">is true</option>
                              <option value="false">is false</option>
                              <option value="empty">is empty</option>
                              <option value="not_empty">is not empty</option>
                              <option value="contains">contains</option>
                              <option value="greater_than">is greater than</option>
                              <option value="less_than">is less than</option>
                            </select>
                          </div>
                          
                          {/* Value Input (only show for operators that need a value) */}
                          {['equals', 'not_equals', 'contains', 'greater_than', 'less_than'].includes(condition.operator) && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Value
                              </label>
                              <input
                                type="text"
                                value={condition.value || ''}
                                onChange={(e) => {
                                  const updatedConditions = [...(variable.conditionalRequirements || [])];
                                  updatedConditions[condIndex] = {
                                    ...updatedConditions[condIndex],
                                    value: e.target.value
                                  };
                                  updateInputVariable(index, 'conditionalRequirements', updatedConditions);
                                }}
                                placeholder="Enter value..."
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          )}
                        </div>
                        
                        {/* Requirement Action */}
                        <div className="mt-2">
                          <label className="flex items-center text-xs">
                            <input
                              type="checkbox"
                              checked={condition.makeRequired !== false}
                              onChange={(e) => {
                                const updatedConditions = [...(variable.conditionalRequirements || [])];
                                updatedConditions[condIndex] = {
                                  ...updatedConditions[condIndex],
                                  makeRequired: e.target.checked
                                };
                                updateInputVariable(index, 'conditionalRequirements', updatedConditions);
                              }}
                              className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                            />
                            <span className="text-gray-700">
                              Make this variable required when condition is met
                            </span>
                          </label>
                        </div>
                        
                        {/* Condition Preview */}
                        {condition.variable && (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                            <strong>If:</strong> {condition.variable} {condition.operator}
                            {['equals', 'not_equals', 'contains', 'greater_than', 'less_than'].includes(condition.operator) && condition.value && (
                              <span> "{condition.value}"</span>
                            )}
                            <br />
                            <strong>Then:</strong> {variable.variableName} will be {condition.makeRequired !== false ? 'required' : 'optional'}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {(variable.conditionalRequirements || []).length === 0 && (
                  <div className="text-xs text-gray-500 italic">
                    No conditional requirements set. This variable's required status is fixed.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Variable Usage Examples */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-medium text-yellow-900 mb-2">💡 Variable Usage Tips</h4>
        <div className="text-sm text-yellow-800 space-y-1">
          <div>• Use <code className="bg-yellow-100 px-1 rounded">{`{{variableName}}`}</code> to reference saved variables</div>
          <div>• For arrays: <code className="bg-yellow-100 px-1 rounded">data[*].id</code> means "extract id from all items"</div>
          <div>• For specific items: <code className="bg-yellow-100 px-1 rounded">data[0].name</code> means "extract name from first item"</div>
          <div>• Use "User Selection" for arrays when users need to choose from a list</div>
          <div>• <strong>AI Extraction:</strong> Use AI models to intelligently extract specific information from user messages</div>
          <div>• Choose specific LLM models for extraction or use project defaults with your API keys</div>
        </div>
      </div>
    </div>
  );
};

export default VariableManager; 